import { useEffect, useState, useMemo } from 'react'
import { 
  Box, 
  Flex, 
  Heading, 
  Text, 
  SimpleGrid, 
  useColorModeValue, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  HStack,
  VStack,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faUsers, 
  faBookOpen, 
  faServer, 
  faDollarSign, 
  faArrowLeft, 
  faSearch, 
  faFilter, 
  faHistory 
} from '@fortawesome/free-solid-svg-icons'
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell } from 'recharts'
import { useAuthenticator } from "@aws-amplify/ui-react"
import { apiGetRequest } from "@/functions/requests"
import { useS3Client } from "@/hooks/contextHooks";
import { S3Client, ListObjectsV2Command } from "@aws-sdk/client-s3";
import { AppMeta } from "@xnav/types/src/common.ts";
import Loading from "@/components/loading";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

function formatBytes(bytes: number, decimals = 2) {
  if (!bytes || !+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

const getLevelTitle = (level: number): string => {
  if (level <= 3) return 'Novice Scribe';
  if (level <= 6) return 'Apprentice Scholar';
  if (level <= 10) return 'Acolyte Researcher';
  if (level <= 15) return 'Knowledge Seeker';
  if (level <= 20) return 'Information Alchemist';
  if (level <= 25) return 'Lore Keeper';
  if (level <= 30) return 'Sage Practitioner';
  if (level <= 35) return 'Elite Scholar';
  if (level <= 40) return 'Academic Explorer';
  if (level <= 50) return 'Master Theoretician';
  if (level <= 60) return 'Grand Archivist';
  if (level <= 70) return 'Lore Chancellor';
  if (level <= 80) return 'Omniscient Sage';
  if (level <= 90) return 'Grandmaster Academic';
  return 'Legendary Sovereign';
};

const getLevelColor = (level: number): string => {
  if (level <= 3) return 'gray';
  if (level <= 6) return 'orange';
  if (level <= 10) return 'green';
  if (level <= 15) return 'cyan';
  if (level <= 20) return 'teal';
  if (level <= 25) return 'yellow';
  if (level <= 30) return 'blue';
  if (level <= 35) return 'messenger';
  if (level <= 40) return 'pink';
  if (level <= 50) return 'teal';
  if (level <= 60) return 'emerald';
  if (level <= 70) return 'emerald';
  if (level <= 80) return 'pink';
  if (level <= 90) return 'teal';
  return 'red';
};

export default function Dashboard() {
  const bg = useColorModeValue('white', 'gray.800')
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.100', 'gray.700')
  const { user } = useAuthenticator()
  const [userRole, setUserRole] = useState<string | null>(null)
  
  const [loading, setLoading] = useState(true)
  const [totalUsers, setTotalUsers] = useState(0)
  const [activeCoursesCount, setActiveCoursesCount] = useState(0)
  const [storageUsed, setStorageUsed] = useState("0 Bytes")
  const [revenue, setRevenue] = useState("$0")
  const [userGrowthData, setUserGrowthData] = useState<any[]>([])
  const [storageByOrgData, setStorageByOrgData] = useState<any[]>([])
  const [contentVolumeData, setContentVolumeData] = useState<any[]>([])
  const [topDownloadsList, setTopDownloadsList] = useState<any[]>([])
  const [topEngagementList, setTopEngagementList] = useState<any[]>([])

  // Gamification & logs states
  const [myAppsList, setMyAppsList] = useState<any[]>([])
  const [courseAccessList, setCourseAccessList] = useState<any[]>([])
  const [viewMode, setViewMode] = useState<'summary' | 'students' | 'logs'>('summary')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterCourseId, setFilterCourseId] = useState('All Courses')

  const s3 = useS3Client()

  useEffect(() => {
    const fetchRole = async () => {
      if (user?.signInDetails?.loginId) {
        try {
          const data = await apiGetRequest<any, { role: string }>(`/users/${user.signInDetails.loginId}`)
          setUserRole(data?.role || 'Instructor')
        } catch (error) {
          console.error("Failed to fetch user role", error)
          setUserRole('Instructor')
        }
      }
    }
    fetchRole()
  }, [user])

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!userRole) return;
      try {
        setLoading(true);

        // 1. Fetch Users
        const usersData = await apiGetRequest<undefined, any[]>('/users');
        const activeUsers = (usersData || []).filter(u => u.email !== 'system_broadcast' && u.email !== 'mobile_banner');
        setTotalUsers(activeUsers.length);

        // 2. Fetch Apps
        const appsData = await apiGetRequest<string, AppMeta[]>('/apps');
        const allApps = appsData || [];
        setActiveCoursesCount(allApps.length);

        // 3. Query S3 for app sizes & fetch mobile analytics for active students
        const s3Client = s3 as S3Client;
        const appsWithMetrics = await Promise.all(allApps.map(async (app) => {
          // Calculate S3 Size
          let totalSize = 0;
          try {
            let isTruncated = true;
            let continuationToken: string | undefined = undefined;
            while (isTruncated) {
              const listCommand: any = new ListObjectsV2Command({
                Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                Prefix: `${app.id}/`,
                ContinuationToken: continuationToken
              });
              const listResult: any = await s3Client.send(listCommand);
              if (listResult.Contents) {
                totalSize += listResult.Contents.reduce((acc: number, obj: any) => acc + (obj.Size || 0), 0);
              }
              isTruncated = listResult.IsTruncated ?? false;
              continuationToken = listResult.NextContinuationToken;
            }
          } catch (e) {
            console.error("Error calculating size for app:", app.name, e);
          }

          // Fetch Active Students (from mobile analytics)
          let studentsCount = 0;
          let analyticsRecords: any[] = [];
          try {
            const analyticsData = await apiGetRequest<undefined, any[]>(`/analytics/mobile/${app.id}`);
            analyticsRecords = analyticsData || [];
            studentsCount = analyticsRecords.length;
          } catch (e) {
            console.error("Error fetching analytics for app:", app.name, e);
          }

          return {
            ...app,
            size: totalSize,
            studentsCount,
            analyticsRecords
          };
        }));

        // Deduplicate apps by ID, keeping the one with actual content (size > 0)
        const uniqueAppsMap = new Map<string, typeof appsWithMetrics[0]>();
        for (const app of appsWithMetrics) {
          const existing = uniqueAppsMap.get(app.id);
          if (!existing || (app.size && app.size > 0)) {
            uniqueAppsMap.set(app.id, app);
          }
        }
        const appsList = Array.from(uniqueAppsMap.values());
        setMyAppsList(appsList);

        // 4. Calculate total storage used
        const totalStorageBytes = appsList.reduce((acc, app) => acc + (app.size || 0), 0);
        setStorageUsed(formatBytes(totalStorageBytes));

        // 5. Calculate storage by organization
        const orgStorageMap = new Map<string, number>();
        appsList.forEach(app => {
          const org = app.orgId?.trim() || 'Others';
          const size = app.size || 0;
          orgStorageMap.set(org, (orgStorageMap.get(org) || 0) + size);
        });

        const sortedOrgs = Array.from(orgStorageMap.entries())
          .map(([name, bytes]) => ({
            name,
            value: parseFloat((bytes / (1024 * 1024 * 1024)).toFixed(3)) // Convert to GB
          }))
          .filter(item => item.value > 0)
          .sort((a, b) => b.value - a.value);

        setStorageByOrgData(sortedOrgs.length > 0 ? sortedOrgs : [{ name: 'No Storage', value: 0.001 }]);

        // 6. User Growth (Active Students by month)
        const monthlyStudentsMap = new Map<string, Set<string>>();
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        // Initialize last 6 months
        const currentDate = new Date();
        const last6Months: string[] = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
          const monthName = months[d.getMonth()];
          last6Months.push(monthName);
          monthlyStudentsMap.set(monthName, new Set<string>());
        }

        appsList.forEach(app => {
          app.analyticsRecords?.forEach((record: any) => {
            if (record.lastActive) {
              const activeDate = new Date(record.lastActive);
              const activeMonthName = months[activeDate.getMonth()];
              if (monthlyStudentsMap.has(activeMonthName)) {
                monthlyStudentsMap.get(activeMonthName)!.add(record.userId);
              }
            }
          });
        });

        // 7. Course Content Volume (New Courses Created by month)
        const monthlyCoursesMap = new Map<string, number>();
        last6Months.forEach(m => monthlyCoursesMap.set(m, 0));

        appsList.forEach(app => {
          if (app.created) {
            const createdDate = new Date(app.created);
            const createdMonthName = months[createdDate.getMonth()];
            if (monthlyCoursesMap.has(createdMonthName)) {
              monthlyCoursesMap.set(createdMonthName, monthlyCoursesMap.get(createdMonthName)! + 1);
            }
          }
        });

        const growthChartData = last6Months.map(month => ({
          name: month,
          students: monthlyStudentsMap.get(month)?.size || 0,
          courses: monthlyCoursesMap.get(month) || 0
        }));

        setUserGrowthData(growthChartData);
        setContentVolumeData(growthChartData);

        // 8. Fetch Course Access Records (Downloads)
        let accessRecords: any[] = [];
        try {
          const accessData = await apiGetRequest<undefined, any[]>('/apps/course-access');
          accessRecords = accessData || [];
        } catch (err) {
          console.error("Failed to fetch course access data:", err);
        }
        setCourseAccessList(accessRecords);

        const downloadsMap = new Map<string, number>();
        accessRecords.forEach(rec => {
          const count = rec.downloadCount || 1;
          downloadsMap.set(rec.courseId, (downloadsMap.get(rec.courseId) || 0) + count);
        });

        // 9. Top Performing Courses (sorted by downloads and engagement)
        const coursesWithMetrics = appsList.map(app => {
          const isDeployed = app.deployed !== undefined;
          const isUpToDate = isDeployed && app.deployed === app.edited;
          const totalScore = app.analyticsRecords?.reduce((acc: number, r: any) => acc + (r.totalScore || 0), 0) || 0;
          const totalQuests = app.analyticsRecords?.reduce((acc: number, r: any) => acc + (r.questsCompleted || 0), 0) || 0;
          return {
            id: app.id,
            name: app.name,
            org: app.orgId || 'Internal',
            students: downloadsMap.get(app.id) || 0,
            score: totalScore,
            quests: totalQuests,
            status: isUpToDate ? 'Live' : 'Edited'
          };
        });

        const topDownloads = [...coursesWithMetrics]
          .sort((a, b) => b.students - a.students)
          .slice(0, 5);

        const topEngagement = [...coursesWithMetrics]
          .sort((a, b) => b.score - a.score)
          .slice(0, 5);

        setTopDownloadsList(topDownloads);
        setTopEngagementList(topEngagement);
        setRevenue("$0.00");
      } catch (e) {
        console.error("Error loading dashboard metrics:", e);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [userRole, s3]);

  // Dynamic user activity tracking across all apps
  const studentActivityList = useMemo(() => {
    const list: {
      userId: string;
      userName: string;
      courseName: string;
      courseId: string;
      questsCompleted: number;
      rightAnswers: number;
      wrongAnswers: number;
      totalScore: number;
      level: number;
      lastActive: string;
    }[] = [];

    myAppsList.forEach(app => {
      app.analyticsRecords?.forEach((record: any) => {
        list.push({
          userId: record.userId,
          userName: record.userName || 'Anonymous User',
          courseName: app.name,
          courseId: app.id,
          questsCompleted: record.questsCompleted || 0,
          rightAnswers: record.rightAnswers || 0,
          wrongAnswers: record.wrongAnswers || 0,
          totalScore: record.totalScore || 0,
          level: record.level || 1,
          lastActive: record.lastActive
        });
      });
    });

    return list.sort((a, b) => new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime());
  }, [myAppsList]);

  const studentProfilesList = useMemo(() => {
    const profilesMap = new Map<string, {
      userId: string;
      userName: string;
      userEmail: string;
      courses: { id: string; name: string; org: string; downloadedAt: string; downloadCount: number }[];
      totalDownloads: number;
      lastActive: string;
    }>();

    courseAccessList.forEach(record => {
      const userId = record.userId;
      const userName = record.userName || record.userEmail || 'Anonymous Student';
      const userEmail = record.userEmail || '';
      const courseInfo = {
        id: record.courseId,
        name: record.name || record.title || 'Course',
        org: record.orgId || record.org || 'Internal',
        downloadedAt: record.downloadedAt || '',
        downloadCount: record.downloadCount || 1
      };

      const existing = profilesMap.get(userId);
      if (existing) {
        const existingCourse = existing.courses.find(c => c.id === record.courseId);
        if (existingCourse) {
          existingCourse.downloadCount = Math.max(existingCourse.downloadCount, record.downloadCount || 1);
          if (record.downloadedAt && (!existingCourse.downloadedAt || new Date(record.downloadedAt).getTime() > new Date(existingCourse.downloadedAt).getTime())) {
            existingCourse.downloadedAt = record.downloadedAt;
          }
        } else {
          existing.courses.push(courseInfo);
        }
        existing.totalDownloads = existing.courses.reduce((sum, c) => sum + c.downloadCount, 0);
        if (record.downloadedAt && (!existing.lastActive || new Date(record.downloadedAt).getTime() > new Date(existing.lastActive).getTime())) {
          existing.lastActive = record.downloadedAt;
        }
      } else {
        profilesMap.set(userId, {
          userId,
          userName,
          userEmail,
          courses: [courseInfo],
          totalDownloads: record.downloadCount || 1,
          lastActive: record.downloadedAt || ''
        });
      }
    });

    return Array.from(profilesMap.values());
  }, [courseAccessList]);

  // Unique active students count
  const enrolledStudentsCount = useMemo(() => {
    return studentProfilesList.length;
  }, [studentProfilesList]);

  const filteredActivityList = useMemo(() => {
    return studentActivityList.filter(act => {
      const matchesSearch = act.userName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCourse = filterCourseId === 'All Courses' || act.courseId === filterCourseId;
      return matchesSearch && matchesCourse;
    });
  }, [studentActivityList, searchQuery, filterCourseId]);

  const filteredProfilesList = useMemo(() => {
    return studentProfilesList.filter(profile => {
      const matchesSearch = profile.userName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCourse = filterCourseId === 'All Courses' || profile.courses.some(c => c.id === filterCourseId);
      return matchesSearch && matchesCourse;
    });
  }, [studentProfilesList, searchQuery, filterCourseId]);

  const StatCard = ({ title, value, icon }: any) => {
    const cardBg = useColorModeValue("white", "#111827");
    const labelColor = useColorModeValue("gray.500", "gray.400");
    const valueColor = useColorModeValue("gray.900", "white");
    const borderCardColor = useColorModeValue("gray.200", "#374151");

    return (
      <Box 
        bg={cardBg} 
        p={5} 
        borderRadius="xl" 
        border="1px solid" 
        borderColor={borderCardColor}
      >
        <Flex align="center" justify="space-between">
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" fontWeight="600" color={labelColor}>
              {title}
            </Text>
            <Heading size="md" fontWeight="800" letterSpacing="-0.5px" color={valueColor}>
              {value}
            </Heading>
          </VStack>
          <Flex 
            w={10} 
            h={10} 
            align="center" 
            justify="center" 
            borderRadius="lg" 
            bg="linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%)" 
            color="#0284c7"
          >
            <FontAwesomeIcon icon={icon} />
          </Flex>
        </Flex>
      </Box>
    )
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box 
          bg={useColorModeValue("rgba(255, 255, 255, 0.9)", "rgba(26, 32, 44, 0.95)")} 
          backdropFilter="blur(10px)"
          border="1px solid"
          borderColor={useColorModeValue("emerald.100", "emerald.900")}
          p={4} 
          borderRadius="xl" 
          shadow="xl"
        >
          <Text fontWeight="bold" color={useColorModeValue("gray.800", "white")} mb={2}>{label}</Text>
          {payload.map((pld: any) => (
            <HStack key={pld.name} spacing={2} py={0.5}>
              <Box w={3} h={3} borderRadius="full" bg={pld.color || pld.stroke} />
              <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.300")}>
                {pld.name}: <Text as="span" fontWeight="extrabold" color={pld.color || pld.stroke}>{pld.value}</Text>
              </Text>
            </HStack>
          ))}
        </Box>
      );
    }
    return null;
  };

  const getAvatarData = (userName: string) => {
    const initials = userName.substring(0, 2).toUpperCase()
    const gradients = [
        "linear(to-br, #0284c7, #10b981)",
        "linear(to-br, #0ea5e9, #06b6d4)",
        "linear(to-br, #06b6d4, #10b981)",
        "linear(to-br, #38bdf8, #34d399)",
        "linear(to-br, #0369a1, #059669)",
    ]
    let sum = 0
    for (let i = 0; i < userName.length; i++) {
        sum += userName.charCodeAt(i)
    }
    const gradient = gradients[sum % gradients.length]
    return { initials, gradient }
  }

  const isSuperAdmin = userRole === 'Super Admin'
  const isOrgAdmin = userRole === 'Org Admin'
  const showAdminDashboard = isSuperAdmin || isOrgAdmin

  if (loading) {
    return <Loading message="Loading dashboard analytics..." />
  }

  // DETAILED STUDENT ACCESS LOGS VIEW
  if (viewMode === 'logs') {
    return (
      <Flex flexDir="column" gap={6} w="100%" h="100%" maxW="1200px" mx="auto" p={5} overflowY="auto">
        <Box display="flex" justifyContent="space-between" alignItems="center" flexDir={{ base: "column", md: "row" }} gap={4}>
          <Box>
            <Heading size="lg" mb={2} bgGradient="linear(to-r, #00DF89, #00B871)" bgClip="text" fontWeight="extrabold">
              Detailed Course Download & Access Logs
            </Heading>
            <Text color="gray.500" fontSize="sm">View and filter active students, quest scores, and course downloads across all modules.</Text>
          </Box>
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme={viewMode === 'summary' ? 'emerald' : 'gray'}
              variant={viewMode === 'summary' ? 'solid' : 'ghost'}
              onClick={() => { setViewMode('summary'); setSearchQuery(''); setFilterCourseId('All Courses'); }}
              borderRadius="full"
            >
              Overview
            </Button>
            <Button
              size="sm"
              colorScheme={viewMode === 'students' ? 'emerald' : 'gray'}
              variant={viewMode === 'students' ? 'solid' : 'ghost'}
              onClick={() => { setViewMode('students'); setSearchQuery(''); setFilterCourseId('All Courses'); }}
              borderRadius="full"
            >
              Student Downloads
            </Button>
            <Button
              size="sm"
              colorScheme={viewMode === 'logs' ? 'emerald' : 'gray'}
              variant={viewMode === 'logs' ? 'solid' : 'ghost'}
              onClick={() => { setViewMode('logs'); setSearchQuery(''); setFilterCourseId('All Courses'); }}
              borderRadius="full"
            >
              Access Logs
            </Button>
          </HStack>
        </Box>

        {/* Filter controls */}
        <Flex 
          w="100%" 
          bg={bg} 
          p={3} 
          borderRadius="2xl" 
          boxShadow="sm"
          border="1px solid" 
          borderColor={borderColor}
          flexDir={{ base: "column", md: "row" }}
          alignItems="center"
          gap={4}
        >
          <InputGroup size="lg" flex={1}>
            <InputLeftElement pointerEvents="none" h="full">
              <FontAwesomeIcon icon={faSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by student username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="transparent"
              border="none"
              _focus={{ boxShadow: "none" }}
              fontSize="md"
              pl={10}
            />
          </InputGroup>

          <Menu>
            <MenuButton 
              as={Button} 
              variant="ghost" 
              borderRadius="full" 
              leftIcon={<FontAwesomeIcon icon={faFilter} color="gray.400" />}
              fontWeight="600"
              color="gray.600"
              _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
            >
              {filterCourseId === 'All Courses' ? 'All Courses' : myAppsList.find(c => c.id === filterCourseId)?.name || 'Course'}
            </MenuButton>
            <MenuList shadow="xl" borderRadius="xl" border="none" maxH="300px" overflowY="auto">
              <MenuItem onClick={() => setFilterCourseId('All Courses')}>All Courses</MenuItem>
              {myAppsList.map(course => (
                <MenuItem key={course.id} onClick={() => setFilterCourseId(course.id)}>
                  {course.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>

        {/* Detailed Logs Table */}
        <Box bg={bg} borderRadius="2xl" shadow="sm" border="1px solid" borderColor={borderColor} overflow="hidden">
          <Table variant="simple">
            <Thead bg={useColorModeValue("gray.50", "gray.900/40")}>
              <Tr>
                <Th py={4}>Student Name</Th>
                <Th py={4}>Course Accessed</Th>
                <Th py={4}>Level & Rank</Th>
                <Th py={4} isNumeric>Quests Completed</Th>
                <Th py={4} isNumeric>Accuracy</Th>
                <Th py={4} isNumeric>Total Score</Th>
                <Th py={4}>Last Active</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredActivityList.length === 0 ? (
                <Tr><Td colSpan={7} textAlign="center" py={10} color="gray.500">No logs match your filter settings.</Td></Tr>
              ) : (
                filteredActivityList.map((log, idx) => {
                  const accuracy = log.rightAnswers + log.wrongAnswers > 0
                    ? Math.round((log.rightAnswers / (log.rightAnswers + log.wrongAnswers)) * 100)
                    : 0;
                  return (
                    <Tr key={idx} _hover={{ bg: useColorModeValue("emerald.50/20", "whiteAlpha.50") }} transition="background 0.2s">
                      <Td fontWeight="bold" color="emerald.500" py={4}>{log.userName}</Td>
                      <Td fontWeight="medium" py={4}>{log.courseName}</Td>
                      <Td py={4}>
                        <Badge colorScheme={getLevelColor(log.level)} borderRadius="full" px={2.5} py={0.5} fontWeight="bold" fontSize="10px">
                          {getLevelTitle(log.level)} (Lvl {log.level})
                        </Badge>
                      </Td>
                      <Td isNumeric fontWeight="medium" py={4}>{log.questsCompleted}</Td>
                      <Td isNumeric py={4}>
                        <Badge colorScheme={accuracy >= 80 ? 'green' : accuracy >= 50 ? 'orange' : 'red'} borderRadius="full" px={2.5} py={0.5} fontSize="10px">
                          {accuracy}%
                        </Badge>
                      </Td>
                      <Td isNumeric fontWeight="bold" color="orange.500" py={4}>{log.totalScore}</Td>
                      <Td fontSize="sm" color="gray.500" py={4}>
                        {new Date(log.lastActive).toLocaleString()}
                      </Td>
                    </Tr>
                  )
                })
              )}
            </Tbody>
          </Table>
        </Box>
      </Flex>
    )
  }

  // DETAILED STUDENT DOWNLOAD PROFILES VIEW
  if (viewMode === 'students') {
    return (
      <Flex flexDir="column" gap={6} w="100%" h="100%" maxW="1200px" mx="auto" p={5} overflowY="auto">
        <Box display="flex" justifyContent="space-between" alignItems="center" flexDir={{ base: "column", md: "row" }} gap={4}>
          <Box>
            <Heading size="lg" mb={2} bgGradient="linear(to-r, #00DF89, #00B871)" bgClip="text" fontWeight="extrabold">
              Student Download Profiles
            </Heading>
            <Text color="gray.500" fontSize="sm">Track which courses each student has downloaded and their current progress.</Text>
          </Box>
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme={viewMode === 'summary' ? 'emerald' : 'gray'}
              variant={viewMode === 'summary' ? 'solid' : 'ghost'}
              onClick={() => { setViewMode('summary'); setSearchQuery(''); setFilterCourseId('All Courses'); }}
              borderRadius="full"
            >
              Overview
            </Button>
            <Button
              size="sm"
              colorScheme={viewMode === 'students' ? 'emerald' : 'gray'}
              variant={viewMode === 'students' ? 'solid' : 'ghost'}
              onClick={() => { setViewMode('students'); setSearchQuery(''); setFilterCourseId('All Courses'); }}
              borderRadius="full"
            >
              Student Downloads
            </Button>
            <Button
              size="sm"
              colorScheme={viewMode === 'logs' ? 'emerald' : 'gray'}
              variant={viewMode === 'logs' ? 'solid' : 'ghost'}
              onClick={() => { setViewMode('logs'); setSearchQuery(''); setFilterCourseId('All Courses'); }}
              borderRadius="full"
            >
              Access Logs
            </Button>
          </HStack>
        </Box>

        {/* Filter controls */}
        <Flex 
          w="100%" 
          bg={bg} 
          p={3} 
          borderRadius="2xl" 
          boxShadow="sm"
          border="1px solid" 
          borderColor={borderColor}
          flexDir={{ base: "column", md: "row" }}
          alignItems="center"
          gap={4}
        >
          <InputGroup size="lg" flex={1}>
            <InputLeftElement pointerEvents="none" h="full">
              <FontAwesomeIcon icon={faSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by student username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="transparent"
              border="none"
              _focus={{ boxShadow: "none" }}
              fontSize="md"
              pl={10}
            />
          </InputGroup>

          <Menu>
            <MenuButton 
              as={Button} 
              variant="ghost" 
              borderRadius="full" 
              leftIcon={<FontAwesomeIcon icon={faFilter} color="gray.400" />}
              fontWeight="600"
              color="gray.600"
              _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
            >
              {filterCourseId === 'All Courses' ? 'All Courses' : myAppsList.find(c => c.id === filterCourseId)?.name || 'Course'}
            </MenuButton>
            <MenuList shadow="xl" borderRadius="xl" border="none" maxH="300px" overflowY="auto">
              <MenuItem onClick={() => setFilterCourseId('All Courses')}>All Courses</MenuItem>
              {myAppsList.map(course => (
                <MenuItem key={course.id} onClick={() => setFilterCourseId(course.id)}>
                  {course.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>

        {/* Student Downloads Table */}
        <Box bg={bg} borderRadius="2xl" shadow="sm" border="1px solid" borderColor={borderColor} overflow="hidden">
          <Table variant="simple">
            <Thead bg={useColorModeValue("gray.50", "gray.900/40")}>
              <Tr>
                <Th py={4}>Student Name</Th>
                <Th py={4}>Downloaded Courses</Th>
                <Th py={4} isNumeric>Total Downloads</Th>
                <Th py={4}>Last Active</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredProfilesList.length === 0 ? (
                <Tr><Td colSpan={4} textAlign="center" py={10} color="gray.500">No student profiles match your filter settings.</Td></Tr>
              ) : (
                filteredProfilesList.map((profile, idx) => {
                  const { initials, gradient } = getAvatarData(profile.userName);
                  return (
                    <Tr key={idx} _hover={{ bg: useColorModeValue("emerald.50/20", "whiteAlpha.50") }} transition="background 0.2s">
                      <Td py={4}>
                        <HStack spacing={3}>
                          <Flex 
                            w={8} 
                            h={8} 
                            borderRadius="full" 
                            bgGradient={gradient} 
                            color="white" 
                            align="center" 
                            justify="center" 
                            fontWeight="bold" 
                            fontSize="xs"
                            shadow="sm"
                          >
                            {initials}
                          </Flex>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" color="emerald.500">{profile.userName}</Text>
                            {profile.userEmail && (
                              <Text fontSize="10px" color="gray.500">{profile.userEmail}</Text>
                            )}
                          </VStack>
                        </HStack>
                      </Td>
                      <Td py={4}>
                        <Flex wrap="wrap" gap={2} maxW="550px">
                          {profile.courses.map(c => (
                            <Box 
                              key={c.id} 
                              bg={useColorModeValue("emerald.50", "emerald.900/20")} 
                              border="1px solid" 
                              borderColor={useColorModeValue("emerald.200", "emerald.800")}
                              borderRadius="lg" 
                              px={3} 
                              py={1.5}
                              fontSize="xs"
                            >
                              <Text fontWeight="bold" color={useColorModeValue("emerald.700", "emerald.300")}>
                                {c.name}
                              </Text>
                              <HStack spacing={1.5} mt={0.5} color="gray.500" fontSize="10px">
                                <Text>Downloads: {c.downloadCount}</Text>
                                {c.downloadedAt && (
                                  <>
                                    <Text>•</Text>
                                    <Text>{new Date(c.downloadedAt).toLocaleString()}</Text>
                                  </>
                                )}
                              </HStack>
                            </Box>
                          ))}
                        </Flex>
                      </Td>
                      <Td isNumeric fontWeight="bold" py={4}>{profile.totalDownloads}</Td>
                      <Td fontSize="sm" color="gray.500" py={4}>
                        {profile.lastActive ? new Date(profile.lastActive).toLocaleString() : 'N/A'}
                      </Td>
                    </Tr>
                  )
                })
              )}
            </Tbody>
          </Table>
        </Box>
      </Flex>
    )
  }

  // STANDARD SUMMARY DASHBOARD VIEW
  return (
    <Flex flexDir="column" gap={6} w="100%" h="100%" maxW="1500px" mx="auto" p={{ base: 4, md: 6 }} overflowY="auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexDir={{ base: "column", md: "row" }} gap={4}>
        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color={useColorModeValue("gray.900", "white")}>
            {isSuperAdmin ? 'Super Admin Dashboard' : isOrgAdmin ? 'Organization Admin Dashboard' : 'Instructor Dashboard'}
          </Heading>
          <Text color={useColorModeValue("gray.500", "gray.400")} fontSize="sm" mt={0.5}>
            {isSuperAdmin ? 'Platform analytics, storage allocation, and live learner activity.' : isOrgAdmin ? 'Organization overview and analytics.' : 'Welcome to your instructor portal.'}
          </Text>
        </Box>
      </Box>

      {showAdminDashboard ? (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
          <StatCard title="Total Users" value={totalUsers} icon={faUsers} />
          <StatCard title="Active Courses" value={activeCoursesCount} icon={faBookOpen} />
          <StatCard title="Cloud Storage" value={storageUsed} icon={faServer} />
        </SimpleGrid>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
          <StatCard title="My Courses" value={myAppsList.length} icon={faBookOpen} />
          <StatCard title="Enrolled Students" value={enrolledStudentsCount} icon={faUsers} />
          <StatCard title="Cloud Storage" value={storageUsed} icon={faServer} />
        </SimpleGrid>
      )}

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mt={4}>
        <Box 
          bg={bg} 
          p={6} 
          borderRadius="2xl" 
          border="1px solid" 
          borderColor={borderColor}
          boxShadow={useColorModeValue("0 4px 25px rgba(0, 0, 0, 0.02)", "0 4px 25px rgba(0, 0, 0, 0.15)")}
          gridColumn={{ lg: "span 2" }}
        >
          <Heading size="md" mb={6} color={useColorModeValue("gray.800", "white")}>User Growth</Heading>
          <Box h="300px">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userGrowthData}>
                <defs>
                  <linearGradient id="colorStudents" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.45}/>
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.45}/>
                  </linearGradient>
                  <linearGradient id="strokeStudents" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#0ea5e9"/>
                    <stop offset="100%" stopColor="#10b981"/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={useColorModeValue("#edf2f7", "#2d3748")} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: useColorModeValue("#718096", "#a0aec0"), fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: useColorModeValue("#718096", "#a0aec0"), fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" />
                <Area type="monotone" dataKey="students" stroke="url(#strokeStudents)" strokeWidth={3.5} fillOpacity={1} fill="url(#colorStudents)" activeDot={{ r: 8, fill: "#0ea5e9", stroke: "#10b981", strokeWidth: 2 }} name="Active Students" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Dynamic student activity list summary (Preview) */}
        <Box 
          bg={bg} 
          p={6} 
          borderRadius="2xl" 
          border="1px solid" 
          borderColor={borderColor}
          boxShadow={useColorModeValue("0 4px 25px rgba(0, 0, 0, 0.02)", "0 4px 25px rgba(0, 0, 0, 0.15)")}
          display="flex" 
          flexDir="column"
        >
          <Heading size="md" mb={4} display="flex" alignItems="center" gap={2} color={useColorModeValue("gray.800", "white")}>
            <FontAwesomeIcon icon={faHistory} color="#0ea5e9" /> Recent Activity
          </Heading>
          <VStack spacing={3} align="stretch" flex={1}>
            {studentActivityList.length === 0 ? (
              <Flex align="center" justify="center" flex={1} minH="150px">
                <Text color="gray.500" fontSize="sm" fontStyle="italic">No downloads or access logs recorded.</Text>
              </Flex>
            ) : (
              studentActivityList.slice(0, 3).map((act, index) => {
                const { initials, gradient } = getAvatarData(act.userName);
                return (
                  <HStack 
                    key={index} 
                    p={3.5} 
                    bg={useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.04) 0%, rgba(16, 185, 129, 0.03) 100%)", "whiteAlpha.50")} 
                    borderRadius="2xl" 
                    border="1px solid" 
                    borderColor={useColorModeValue("gray.100", "gray.750")}
                    spacing={3}
                    _hover={{ transform: "translateX(4px)", borderColor: "#38bdf8" }}
                    transition="all 0.2s"
                  >
                    <Flex 
                      w={9} 
                      h={9} 
                      borderRadius="full" 
                      bgGradient={gradient} 
                      color="white" 
                      align="center" 
                      justify="center" 
                      fontWeight="bold" 
                      fontSize="xs"
                      shadow="sm"
                      flexShrink={0}
                    >
                      {initials}
                    </Flex>
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <Flex justify="space-between" align="center" w="100%">
                        <Text fontWeight="bold" fontSize="sm" color={useColorModeValue("gray.800", "white")} isTruncated pr={2}>
                          {act.userName}
                        </Text>
                        <Badge colorScheme={getLevelColor(act.level)} fontSize="8px" borderRadius="full">
                          {getLevelTitle(act.level)}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center" w="100%" fontSize="11px" color="gray.500" mt={0.5}>
                        <Text isTruncated pr={2}>{act.courseName}</Text>
                        <Text whiteSpace="nowrap">{new Date(act.lastActive).toLocaleDateString()}</Text>
                      </Flex>
                    </VStack>
                  </HStack>
                )
              })
            )}
          </VStack>
        </Box>
      </SimpleGrid>

      {showAdminDashboard && (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mt={2} mb={8}>
          <Box 
            bg={bg} 
            p={6} 
            borderRadius="2xl" 
            border="1px solid" 
            borderColor={borderColor}
            boxShadow={useColorModeValue("0 4px 25px rgba(0, 0, 0, 0.02)", "0 4px 25px rgba(0, 0, 0, 0.15)")}
          >
            <Heading size="md" mb={6} color={useColorModeValue("gray.800", "white")}>Course Content Volume</Heading>
            <Box h="300px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={contentVolumeData}>
                  <defs>
                    <linearGradient id="colorCourses" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3182ce" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3182ce" stopOpacity={0.3}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={useColorModeValue("#edf2f7", "#2d3748")} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: useColorModeValue("#718096", "#a0aec0"), fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: useColorModeValue("#718096", "#a0aec0"), fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" />
                  <Bar dataKey="courses" fill="url(#colorCourses)" radius={[6, 6, 0, 0]} name="New Courses Deployed" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box 
            bg={bg} 
            p={6} 
            borderRadius="2xl" 
            border="1px solid" 
            borderColor={borderColor}
            boxShadow={useColorModeValue("0 4px 25px rgba(0, 0, 0, 0.02)", "0 4px 25px rgba(0, 0, 0, 0.15)")}
            overflowX="auto"
          >
            <Tabs variant="unstyled" size="sm">
              <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
                <Heading size="md" color={useColorModeValue("gray.800", "white")}>Course Leaderboards</Heading>
                <TabList bg={useColorModeValue("gray.100", "gray.900")} p={1} borderRadius="full" gap={1} border="1px solid" borderColor={borderColor}>
                  <Tab
                    _selected={{
                      bgGradient: "linear(to-r, #00DF89, #00B871)",
                      color: "#0B192C",
                      shadow: "0px 4px 14px rgba(0, 223, 137, 0.4)"
                    }}
                    borderRadius="full"
                    fontWeight="bold"
                    px={4}
                    py={1.5}
                    fontSize="xs"
                  >
                    Most Downloaded
                  </Tab>
                  <Tab
                    _selected={{
                      bgGradient: "linear(to-r, #00DF89, #00B871)",
                      color: "#0B192C",
                      shadow: "0px 4px 14px rgba(0, 223, 137, 0.4)"
                    }}
                    borderRadius="full"
                    fontWeight="bold"
                    px={4}
                    py={1.5}
                    fontSize="xs"
                  >
                    Top Engagement
                  </Tab>
                </TabList>
              </Flex>
              <TabPanels>
                <TabPanel p={0}>
                  <Table variant="simple" size="sm">
                    <Thead bg={useColorModeValue("gray.50", "gray.900/40")}>
                      <Tr>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider">Course Name</Th>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider">Organization</Th>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" isNumeric>Unique Downloads</Th>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider">Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {topDownloadsList.map((course) => (
                        <Tr 
                          key={course.id} 
                          _hover={{ bg: useColorModeValue("emerald.50/20", "whiteAlpha.50") }} 
                          transition="background 0.2s"
                        >
                          <Td fontWeight="semibold" py={4}>{course.name}</Td>
                          <Td py={4}><Badge colorScheme="gray" borderRadius="md" px={2} py={0.5}>{course.org}</Badge></Td>
                          <Td isNumeric fontWeight="bold" color="emerald.500" py={4}>{course.students}</Td>
                          <Td py={4}>
                            <Badge colorScheme={course.status === 'Live' ? 'green' : 'yellow'} variant="subtle" borderRadius="full" px={2.5} py={0.5}>
                              {course.status}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>
                <TabPanel p={0}>
                  <Table variant="simple" size="sm">
                    <Thead bg={useColorModeValue("gray.50", "gray.900/40")}>
                      <Tr>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider">Course Name</Th>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider">Organization</Th>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" isNumeric>Engagement Score</Th>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" isNumeric>Quests Done</Th>
                        <Th py={3} fontSize="10px" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider">Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {topEngagementList.map((course) => (
                        <Tr 
                          key={course.id} 
                          _hover={{ bg: useColorModeValue("emerald.50/20", "whiteAlpha.50") }} 
                          transition="background 0.2s"
                        >
                          <Td fontWeight="semibold" py={4}>{course.name}</Td>
                          <Td py={4}><Badge colorScheme="gray" borderRadius="md" px={2} py={0.5}>{course.org}</Badge></Td>
                          <Td isNumeric fontWeight="bold" color="emerald.500" py={4}>{course.score}</Td>
                          <Td isNumeric fontWeight="medium" py={4}>{course.quests}</Td>
                          <Td py={4}>
                            <Badge colorScheme={course.status === 'Live' ? 'green' : 'yellow'} variant="subtle" borderRadius="full" px={2.5} py={0.5}>
                              {course.status}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </SimpleGrid>
      )}
    </Flex>
  )
}
